import { booleanValid, kinks, polygon } from '@turf/turf';

const METERS_PER_DEGREE_LATITUDE = 111320;
const EDGE_TOLERANCE_METERS = 10;
const MINIMUM_VERTEX_SEPARATION_METERS = 4;

export const isValidEditablePolygon = (coordinates = []) => {
  if (!Array.isArray(coordinates) || coordinates.length < 3) return false;
  try {
    const positions = coordinates.map(({ lat, lng }) => [Number(lng), Number(lat)]);
    const feature = polygon([[...positions, positions[0]]]);
    return booleanValid(feature) && kinks(feature).features.length === 0;
  } catch {
    return false;
  }
};

export const getNearestPolygonEdgePoint = (
  coordinates,
  pointer,
  { maximumDistanceMeters = EDGE_TOLERANCE_METERS } = {},
) => {
  if (!Array.isArray(coordinates) || coordinates.length < 2 || !pointer) return null;
  const longitudeScale = Math.max(Math.cos((pointer.lat * Math.PI) / 180), 0.01);
  let nearest = null;

  coordinates.forEach((coordinate, index) => {
    const nextCoordinate = coordinates[(index + 1) % coordinates.length];
    const edgeX = (nextCoordinate.lng - coordinate.lng) * longitudeScale;
    const edgeY = nextCoordinate.lat - coordinate.lat;
    const pointerX = (pointer.lng - coordinate.lng) * longitudeScale;
    const pointerY = pointer.lat - coordinate.lat;
    const edgeLengthSquared = edgeX * edgeX + edgeY * edgeY || 1;
    const progress = Math.max(
      0,
      Math.min(1, (pointerX * edgeX + pointerY * edgeY) / edgeLengthSquared),
    );
    const position = {
      lat: coordinate.lat + (nextCoordinate.lat - coordinate.lat) * progress,
      lng: coordinate.lng + (nextCoordinate.lng - coordinate.lng) * progress,
    };
    const distanceSquared = (pointerX - edgeX * progress) ** 2 + (pointerY - edgeY * progress) ** 2;
    if (!nearest || distanceSquared < nearest.distanceSquared) {
      nearest = { position, insertIndex: index + 1, distanceSquared };
    }
  });

  if (
    !nearest ||
    nearest.distanceSquared * METERS_PER_DEGREE_LATITUDE ** 2 > maximumDistanceMeters ** 2
  )
    return null;

  const isTooClose = coordinates.some((coordinate) => {
    const latDistance = nearest.position.lat - coordinate.lat;
    const lngDistance = (nearest.position.lng - coordinate.lng) * longitudeScale;
    return (
      (latDistance ** 2 + lngDistance ** 2) * METERS_PER_DEGREE_LATITUDE ** 2 <
      MINIMUM_VERTEX_SEPARATION_METERS ** 2
    );
  });
  return isTooClose ? null : nearest;
};
